import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { LsqApiServiceListSource, Pagination } from '@lsq-frontend/angular-shared/api-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListSellerSummaryRequest,
    ListSellerSummaryService,
    ListSellerSummaryForBuyerAccountRequest,
    ListSellerSummaryForBuyerAccountService,
    ListSellerSummaryForBuyerAccountResponse
} from '@lsq-frontend/remote-services/financing-graphql';
import { ClaimService } from '@lsq-frontend/common-ui';
import { isNull } from 'lodash';
import { SupplyChainDirectAccountService } from '../supply-chain-direct-account.service';

@Injectable()
export class SuppliersDataSourceService {
    public readonly filtersFormGroup = new FormGroup({
        sellerNameFilter: new FormControl(''),
        sellerEnrollmentStatusFilter: new FormControl([])
    });

    public listSuppliersDataSource: LsqApiServiceListSource<
        any,
        any,
        ListSellerSummaryForBuyerAccountResponse['rows'][0]
    > | null = null;

    constructor(
        private _userService: UserService,
        private _claimService: ClaimService,
        private _listSuppliersForAccountService: ListSellerSummaryForBuyerAccountService,
        private _listSuppliersForBuyerService: ListSellerSummaryService,
        private _supplyChainDirectAccountService: SupplyChainDirectAccountService
    ) {
        this._userService.user$.subscribe(() => {
            this.listSuppliersDataSource = null;
            this.filtersFormGroup.reset();
        });

        this._userService.logout$.subscribe((value) => {
            if (value) {
                this.listSuppliersDataSource = null;
            }
        });
    }

    public initializeSuppliersListSource(
        buyerPaidEarlyPaymentFeePercentageVisible?: boolean
    ): void {
        const isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
        if (isNull(this.listSuppliersDataSource)) {
            this.filtersFormGroup.reset();
            this.listSuppliersDataSource = isFinancingAccountPortalUser
                ? // TODO: Implement for Buyer Account
                  new LsqApiServiceListSource(
                      this._listSuppliersForAccountService,
                      new ListSellerSummaryForBuyerAccountRequest({
                          buyerPaidEarlyPaymentFeePercentageVisible,
                          buyerKeysFilter:
                              this._supplyChainDirectAccountService.buyerKeysFormGroup.get(
                                  'buyerKeys'
                              )?.value ?? [],
                          sellerEnrollmentStatusFilter:
                              this.filtersFormGroup.get('sellerEnrollmentStatusFilter')?.value ??
                              [],
                          sellerNameFilter: this.filtersFormGroup.get('sellerNameFilter')?.value,
                          pagination: {
                              take: 25,
                              skip: 0
                          } as Pagination
                      })
                  )
                : new LsqApiServiceListSource(
                      this._listSuppliersForBuyerService,
                      new ListSellerSummaryRequest({
                          refBuyerKeys: [this._getAlternateCompanyKey()],
                          buyerPaidEarlyPaymentFeePercentageVisible,
                          sellerEnrollmentStatusFilter:
                              this.filtersFormGroup.get('sellerEnrollmentStatusFilter')?.value ??
                              [],
                          sellerNameFilter: this.filtersFormGroup.get('sellerNameFilter')?.value,
                          pagination: {
                              take: 25,
                              skip: 0
                          } as Pagination
                      })
                  );
        } else {
            // We need ensure we refetch with the original request.
            // Because ListSellerSummaryService and ListSellerSummaryForBuyerAccountService are
            // singletons, if another page uses those same services, the response will be cached
            // in the ApiService. Dispatching allows us to make sure we get the data we want.
            this.listSuppliersDataSource.dispatchRequest();
        }
    }

    private _getAlternateCompanyKey(): string {
        return this._userService.getAlternateCompanyKey();
    }
}
